import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiAccountCash, mdiAlertCircle, mdiChartBar, mdiBankTransfer, mdiChevronDown, mdiChevronRight, mdiClipboardTextOutline, mdiHelpCircle, mdiHelpCircleOutline, mdiRefresh } from '@mdi/js';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from '../components/AppHeader';
import FinancesHelpModal from '../components/FinancesHelpModal';
import ConnectOnboarding from '../components/finances/ConnectOnboarding';
import { paymentApi } from '../api/payment';
import { organizationApi } from '../api/organization';
import '../pages/Common.css';
import '../pages/Dashboard.css';
import '../pages/header.css';
import './ManageFinances.css';

// Accordion-header content per connect state. Title/subtitle read as a nav card.
// Pill colours follow the site's status-pill convention: green = done (active),
// blue = in progress (account started, Stripe onboarding not yet finished),
// amber = action needed / not started. Classes scoped under .manage-finances-page.
const PAYOUT_STATUS = {
    active: {
        title: 'Bank account',
        subtitle: 'Connected and receiving payouts.',
        pillLabel: 'Active',
        pillClass: 'status-pill--active',
    },
    // Account created on Stripe but onboarding not complete — an intermediate
    // "in progress" state, not a fresh "do this now" one.
    restricted: {
        title: 'Finish Stripe onboarding',
        subtitle: 'Your details were received. Complete Stripe onboarding to start receiving payouts.',
        pillLabel: 'Onboarding incomplete',
        pillClass: 'status-pill--progress',
    },
    notConnected: {
        title: 'Link your bank account',
        subtitle: 'Connect your bank to receive payouts for tickets and bookings.',
        pillLabel: 'Account not linked',
        pillClass: 'status-pill--action',
    },
    // Shown while the real status is unknown (first paint) and for the propagation
    // window straight after hosted onboarding, where Stripe still reports
    // 'restricted' for a few seconds. Rendering the restricted copy there tells
    // someone who just finished onboarding to go and finish it.
    finalising: {
        title: 'Finalising your bank account',
        subtitle: 'Stripe is confirming your details. This usually takes a few seconds.',
        pillLabel: 'Finalising',
        pillClass: 'status-pill--progress',
    },
    unavailable: {
        title: 'Payment status unavailable',
        subtitle: 'We could not check your payout account just now.',
        pillLabel: 'Check failed',
        pillClass: 'status-pill--action',
    },
};

// Re-check schedule after returning from hosted onboarding, in ms BETWEEN attempts.
// Stripe flips the account's capabilities to active shortly AFTER it redirects back,
// so a single read on return reliably catches the pre-activation state.
//
// Cadence matters as much as total duration. This was [0, 2000, 5000, 10000] — attempts
// at 0/2/7/17s — which left a TEN SECOND silence between the 3rd and 4th check. Observed
// live 2026-09-07: the account activated around 8s and the card sat on "Finalising" until
// the 17s check caught up, long enough for the tester to call it stuck. Same ~19s ceiling
// now, but no gap wider than 5s, so the UI trails Stripe by a few seconds rather than ten.
// These are cheap authenticated GETs on the return path only.
const POST_ONBOARDING_POLL_MS = [0, 2000, 3000, 3000, 3000, 3000, 5000];

/**
 * ManageFinances - Portal for financial reporting and reconciliation
 * Portal type: Sub-Portal Feature (.sub-portal-header-card)
 *
 * Payout (bank-details) setup lives here rather than in Settings: a bank account
 * can change over time, whereas Settings is for rarely-changed org config like
 * the base currency. The currency drives which payout countries are offered.
 */
const ManageFinances = () => {
    const navigate = useNavigate();
    const { checkPermission, user } = useAuth();

    const [connectStatus, setConnectStatus] = useState(null);
    const [connectStatusError, setConnectStatusError] = useState(null);
    // Starts true: the status is genuinely unknown before the first fetch resolves, and
    // starting false makes the render guards below no-ops on the first paint.
    const [connectStatusLoading, setConnectStatusLoading] = useState(true);
    // True while the post-onboarding re-check is running (see the return effect).
    const [finalisingPayouts, setFinalisingPayouts] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [baseCurrency, setBaseCurrency] = useState('AUD');
    // null until status loads, then set "smart" once: collapsed if active,
    // expanded if there's setup/verification still to do. User can toggle after.
    const [payoutsExpanded, setPayoutsExpanded] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    // Guards against a stale response overwriting a newer one. Several fetches can be in
    // flight at once (mount + return flow + StrictMode's double-invoke in dev), and
    // without this the LAST to resolve wins rather than the LATEST issued — which is how
    // a pre-onboarding 'restricted' could land on top of a newer 'active'.
    const statusRequestSeq = useRef(0);
    const isMountedRef = useRef(true);
    // Set on mount as well as cleared on unmount: a ref survives React's
    // mount/unmount/remount of the same instance, so clearing it on unmount only
    // leaves it permanently false after a remount and every state setter guarded by
    // it becomes a no-op — the page then sticks on its interim "Finalising" state.
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // Returns the fetched status so the post-onboarding poll can decide whether to stop.
    const loadConnectStatus = useCallback(async () => {
        if (!user?.organisationId) {
            // Deliberately leaves the loading flag set. This path is the transient window
            // where AuthContext has not resolved the user yet, and clearing it here would
            // drop the card straight to "Link your bank account" for a frame — the exact
            // flash this change exists to remove. It cannot strand the card either: the
            // whole block is gated on checkPermission('viewFinancialReports'), which is
            // false without a resolved user, so there is no render with a permission but
            // no org id.
            return null;
        }
        const seq = ++statusRequestSeq.current;
        try {
            setConnectStatusLoading(true);
            const data = await paymentApi.getConnectStatus(user.organisationId);
            if (seq !== statusRequestSeq.current || !isMountedRef.current) return data;
            setConnectStatus(data);
            setConnectStatusError(null);
            setPayoutsExpanded(prev => (prev === null ? data?.status !== 'active' : prev));
            return data;
        } catch (error) {
            console.error('Error loading connect status:', error);
            if (seq === statusRequestSeq.current && isMountedRef.current) {
                setConnectStatusError(error?.message || 'Unable to load payout status. Please try again.');
                setPayoutsExpanded(prev => (prev === null ? true : prev));
            }
            return null;
        } finally {
            if (seq === statusRequestSeq.current && isMountedRef.current) {
                setConnectStatusLoading(false);
            }
        }
    }, [user?.organisationId]);

    // Captured once, at first render. The return effect strips the query param, so
    // reading searchParams later would flip this back to false and let the mount effect
    // fire a competing fetch in the middle of the poll.
    const returnFlowOwnsFetchRef = useRef(null);
    if (returnFlowOwnsFetchRef.current === null) {
        returnFlowOwnsFetchRef.current =
            searchParams.get('onboarded') === 'true' || searchParams.get('refresh') === 'true';
    }
    const postOnboardingPollStartedRef = useRef(false);

    useEffect(() => {
        // On the return flow the effect below owns the refresh, so that the two do not
        // race and land on different answers.
        if (returnFlowOwnsFetchRef.current) return;
        loadConnectStatus();
    }, [loadConnectStatus]);

    // Stripe's hosted onboarding returns to /manage-finances?onboarded=true (or
    // ?refresh=true). That is an SPA navigation to the route the user was ALREADY on,
    // so the component never remounts and the mount-effect above does not re-run — the
    // page would keep showing the pre-onboarding status and tell someone who just
    // finished to go and finish.
    //
    // One re-fetch is not enough: Stripe activates the account's capabilities a few
    // seconds AFTER redirecting back, so a single read on return sees 'restricted' and
    // nothing re-checks it. Poll instead, and hold an interim state while polling.
    //
    // The ref guard (not an effect cleanup) is what keeps the poll alive: stripping the
    // query param changes searchParams, which re-runs this effect, and a cleanup would
    // cancel the poll it had only just started.
    useEffect(() => {
        if (postOnboardingPollStartedRef.current) return;
        if (searchParams.get('onboarded') !== 'true' && searchParams.get('refresh') !== 'true') return;
        postOnboardingPollStartedRef.current = true;

        // Strip the flag (replace, so Back does not land on a stale one) to stop a manual
        // reload re-triggering the poll.
        const next = new URLSearchParams(searchParams);
        next.delete('onboarded');
        next.delete('refresh');
        setSearchParams(next, { replace: true });

        (async () => {
            setFinalisingPayouts(true);
            for (const delay of POST_ONBOARDING_POLL_MS) {
                if (delay) await new Promise(resolve => setTimeout(resolve, delay));
                if (!isMountedRef.current) return;
                const data = await loadConnectStatus();
                if (!isMountedRef.current) return;
                if (data?.status === 'active') break;
            }
            // Give up quietly and show whatever the last read actually said — an account
            // can legitimately still need verification, and that must not be masked.
            if (isMountedRef.current) setFinalisingPayouts(false);
        })();
    }, [searchParams, setSearchParams, loadConnectStatus]);

    useEffect(() => {
        if (!user?.organisationId) return;
        organizationApi
            .getOrgProfile(user.organisationId)
            .then(org => setBaseCurrency(org?.currency || 'AUD'))
            .catch(error => console.error('Error loading org currency:', error));
    }, [user?.organisationId]);

    const canManagePayouts = checkPermission('viewFinancialReports');

    return (
        <div className="dashboard-container org-console manage-finances-page">
            <FinancesHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper" style={{ maxWidth: '900px' }}>
                    {/* Portal Header Card */}
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="back-nav-btn">
                                ← Dashboard
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon path={mdiAccountCash} size={1.2} color="var(--dl-color-purple-default)" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Manage Finances
                            </h1>
                            <div style={{ width: '100px' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <p className="sub-portal-header-subtitle" style={{ margin: 0 }}>
                                Studio finances, reporting and bank reconciliation.
                            </p>
                            <button
                                className="classes-help-trigger"
                                onClick={() => setHelpOpen(true)}
                                onMouseEnter={() => setHelpIconHovered(true)}
                                onMouseLeave={() => setHelpIconHovered(false)}
                                aria-label="Finances Help"
                                title="How It Works">
                                <Icon
                                    path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                    size="1.5rem"
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>
                        </div>
                    </div>

                    {/* Navigation Cards */}
                    <div className="module-grid-row module-grid-row--no-stretch">
                        {/* Finances — owners only */}
                        {checkPermission('viewFinancialReports') && (
                            <div className="manage-card" onClick={() => navigate('/finances')}>
                                <div className="module-card-header">
                                    <h3 style={{ color: 'var(--dl-color-purple-default)', fontSize: '1.25rem', margin: 0 }}>Finances</h3>
                                    <Icon path={mdiAccountCash} size={1.1} color="var(--dl-color-purple-default)" />
                                </div>
                                <p className="module-card-subtitle">Earnings, transactions and payout history</p>
                            </div>
                        )}

                        {/* Reports */}
                        <div className="manage-card" onClick={() => navigate('/reports')}>
                            <div className="module-card-header">
                                <h3 style={{ color: 'var(--dl-color-purple-default)', fontSize: '1.25rem', margin: 0 }}>Reports</h3>
                                <Icon path={mdiChartBar} size={1.1} color="var(--dl-color-purple-default)" />
                            </div>
                            <p className="module-card-subtitle">Attendance, revenue and performance reports</p>
                        </div>

                        {/* Reconcile Payments — owners with viewFinancialReports only */}
                        {checkPermission('viewFinancialReports') && (
                            <div className="manage-card" onClick={() => navigate('/reconcile')}>
                                <div className="module-card-header">
                                    <h3 style={{ color: 'var(--dl-color-purple-default)', fontSize: '1.25rem', margin: 0 }}>Reconcile Payments</h3>
                                    <Icon path={mdiBankTransfer} size={1.1} color="var(--dl-color-purple-default)" />
                                </div>
                                <p className="module-card-subtitle">Match awaiting payments to your bank statement, cash receipts, or other payment methods</p>
                            </div>
                        )}
                    </div>

                    {/* Payout setup — owners only. Below the main tools because it is
                        usually set once. Looks like a nav card, behaves as an accordion
                        with a status pill so the owner sees their state without expanding.
                        Lives here (not Settings) because a bank account can change, whereas
                        Settings is for rarely-changed config like the base currency. */}
                    {canManagePayouts && (() => {
                        // Before the first fetch resolves there is no status to report, and
                        // falling through to notConnected paints "Link your bank account" at
                        // someone who may already be connected.
                        const statusUnknown = connectStatusLoading && !connectStatus;
                        // 'active' wins immediately so a successful poll shows the real
                        // result without waiting for the interim flag to clear.
                        const state = connectStatus?.status === 'active'
                            ? PAYOUT_STATUS.active
                            : (statusUnknown || finalisingPayouts)
                                ? PAYOUT_STATUS.finalising
                                : connectStatus?.status === 'restricted'
                                    ? PAYOUT_STATUS.restricted
                                    : connectStatusError && !connectStatus
                                        ? PAYOUT_STATUS.unavailable
                                        : PAYOUT_STATUS.notConnected;
                        const settling = statusUnknown || finalisingPayouts;
                        const statusUnavailable = !!connectStatusError && !connectStatus && !settling;
                        const expanded = payoutsExpanded === true;
                        return (
                            <div className="manage-finances-payouts content-card">
                                <button
                                    type="button"
                                    className="payouts-accordion-header"
                                    aria-expanded={expanded}
                                    onClick={() => setPayoutsExpanded(prev => !prev)}
                                >
                                    <Icon path={mdiClipboardTextOutline} size={1.1} color="var(--dl-color-purple-default)" className="payouts-accordion-icon" />
                                    <span className="payouts-accordion-text">
                                        <span className="payouts-accordion-title">{state.title}</span>
                                        <span className="payouts-accordion-subtitle">{state.subtitle}</span>
                                    </span>
                                    {/* Always rendered: `state` now carries an interim value while
                                        the status is unknown or settling, so there is no window
                                        where the pill would contradict the title beside it. */}
                                    <span className={`status-pill ${state.pillClass}`}>{state.pillLabel}</span>
                                    <Icon path={expanded ? mdiChevronDown : mdiChevronRight} size={0.85} color="var(--dl-color-gray-500)" className="payouts-accordion-chevron" />
                                </button>
                                {expanded && (
                                    <div className="payouts-accordion-body">
                                        {settling ? (
                                            <p style={{ color: 'var(--dl-color-text-muted)', fontSize: '14px' }}>
                                                {finalisingPayouts
                                                    ? 'Finalising your account with Stripe. This page updates on its own, no need to refresh.'
                                                    : 'Loading payment status...'}
                                            </p>
                                        ) : statusUnavailable ? (
                                            <div className="payout-status-error">
                                                <Icon path={mdiAlertCircle} size={0.95} color="var(--dl-color-danger)" />
                                                <p>{connectStatusError}</p>
                                                <button type="button" className="connect-action-btn" onClick={loadConnectStatus}>
                                                    <Icon path={mdiRefresh} size={0.8} />
                                                    <span>Try Again</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <ConnectOnboarding
                                                connectStatus={connectStatus}
                                                organisationId={user?.organisationId}
                                                baseCurrency={baseCurrency}
                                                compact
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

export default ManageFinances;
